import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [status, setStatus] = useState("loading"); // "loading" | "authed" | "guest"

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setWorkspace(null);
      setStatus("guest");
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspace(data.workspace);
      setStatus("authed");
      return data;
    } catch {
      setToken(null);
      setUser(null);
      setWorkspace(null);
      setStatus("guest");
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    setWorkspace(data.workspace);
    setStatus("authed");
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setToken(data.token);
    setUser(data.user);
    setWorkspace(data.workspace);
    setStatus("authed");
    return data;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setWorkspace(null);
    setStatus("guest");
  }, []);

  const value = useMemo(
    () => ({ user, workspace, status, login, register, logout, refresh }),
    [user, workspace, status, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
